import { Blog } from "../types";
import { slugify } from "../utils/helpers";

const BASE_URL = "https://www.shashi-sharma.com.np";
const TWITTER_HANDLE = "@shashisharma";
const SITE_NAME = "Shashi Sharma - Tech Blog & Portfolio";

interface SEOMetaTags {
  title: string;
  description: string;
  keywords: string;
  canonical: string;
  ogTitle: string;
  ogDescription: string;
  ogUrl: string;
  ogType: string;
  ogImage?: string;
  twitterCard: string;
  twitterTitle: string;
  twitterDescription: string;
  twitterImage?: string;
  structuredData?: string;
}

const toAbsoluteUrl = (maybeRelativeUrl?: string): string | undefined => {
  if (!maybeRelativeUrl) return undefined;
  if (/^https?:\/\//i.test(maybeRelativeUrl)) return maybeRelativeUrl;
  if (maybeRelativeUrl.startsWith("/")) return `${BASE_URL}${maybeRelativeUrl}`;
  return maybeRelativeUrl;
};

export const generateBlogSEO = (
  blog: Blog,
  articleContent: string
): SEOMetaTags => {
  const description = blog.excerpt.substring(0, 155);
  const keywords = blog.tags.join(", ");
  const url = `${BASE_URL}/blog/${slugify(blog.title)}`;
  const imageUrl = toAbsoluteUrl(blog.coverImage);

  const structuredData = {
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    headline: blog.title,
    description: blog.excerpt,
    image: imageUrl,
    datePublished: blog.publishedAt.toISOString(),
    author: {
      "@type": "Person",
      name: blog.author,
    },
    publisher: {
      "@type": "Person",
      name: "Shashi Sharma",
    },
    mainEntityOfPage: {
      "@type": "WebPage",
      "@id": url,
    },
    wordCount: articleContent.split(/\s+/).length,
    timeRequired: `PT${blog.readTimeMinutes}M`,
    articleSection: blog.category || "Technology",
    keywords: keywords,
  };

  return {
    title: `${blog.title} | Shashi Sharma`,
    description,
    keywords,
    canonical: url,
    ogTitle: blog.title,
    ogDescription: description,
    ogUrl: url,
    ogType: "article",
    ogImage: imageUrl,
    twitterCard: "summary_large_image",
    twitterTitle: blog.title,
    twitterDescription: description,
    twitterImage: imageUrl,
    structuredData: JSON.stringify(structuredData),
  };
};

export const generateHomepageSEO = (): SEOMetaTags => {
  return {
    title: "Shashi Sharma - Professional Software Developer",
    description:
      "Professional software developer sharing insights on mobile development, architecture, and software engineering. Explore articles, projects, and more.",
    keywords:
      "Flutter, Dart, Mobile Development, React, TypeScript, Software Engineering, Tech Blog",
    canonical: BASE_URL,
    ogTitle: SITE_NAME,
    ogDescription:
      "Professional Software Developer - Sharing knowledge about mobile development and software engineering",
    ogUrl: BASE_URL,
    ogType: "website",
    twitterCard: "summary",
    twitterTitle: SITE_NAME,
    twitterDescription: "Professional Software Developer",
  };
};

export const generatePortfolioSEO = (): SEOMetaTags => {
  return {
    title: "Portfolio - Shashi Sharma",
    description:
      "Explore my portfolio of Flutter, React, and mobile development projects. See what I've built and the technologies I use.",
    keywords:
      "Portfolio, Projects, Flutter, React, Mobile Apps, Web Development",
    canonical: `${BASE_URL}/portfolio`,
    ogTitle: "Portfolio - Shashi Sharma",
    ogDescription: "My portfolio of mobile and web development projects",
    ogUrl: `${BASE_URL}/portfolio`,
    ogType: "website",
    twitterCard: "summary",
    twitterTitle: "Portfolio - Shashi Sharma",
    twitterDescription: "My portfolio of mobile and web development projects",
  };
};

export const updateMetaTags = (seo: SEOMetaTags) => {
  // Update title
  document.title = seo.title;

  // Update or create meta tags
  const updateMeta = (name: string, content: string, isProperty = false) => {
    const attr = isProperty ? "property" : "name";
    let element = document.querySelector(`meta[${attr}="${name}"]`);

    if (!element) {
      element = document.createElement("meta");
      element.setAttribute(attr, name);
      document.head.appendChild(element);
    }

    element.setAttribute("content", content);
  };

  // Basic meta tags
  updateMeta("description", seo.description);
  updateMeta("keywords", seo.keywords);

  // Canonical URL
  let canonical = document.querySelector(
    'link[rel="canonical"]'
  ) as HTMLLinkElement;
  if (!canonical) {
    canonical = document.createElement("link");
    canonical.rel = "canonical";
    document.head.appendChild(canonical);
  }
  canonical.href = seo.canonical;

  // Open Graph tags
  updateMeta("og:title", seo.ogTitle, true);
  updateMeta("og:description", seo.ogDescription, true);
  updateMeta("og:url", seo.ogUrl, true);
  updateMeta("og:type", seo.ogType, true);
  if (seo.ogImage) {
    updateMeta("og:image", seo.ogImage, true);
  }

  // Twitter Card tags
  updateMeta("twitter:card", seo.twitterCard);
  updateMeta("twitter:title", seo.twitterTitle);
  updateMeta("twitter:description", seo.twitterDescription);
  updateMeta("twitter:site", TWITTER_HANDLE);
  updateMeta("twitter:creator", TWITTER_HANDLE);
  if (seo.twitterImage) {
    updateMeta("twitter:image", seo.twitterImage);
  }

  // Structured data
  if (seo.structuredData) {
    let script = document.querySelector('script[type="application/ld+json"]');
    if (!script) {
      script = document.createElement("script");
      script.setAttribute("type", "application/ld+json");
      document.head.appendChild(script);
    }
    script.textContent = seo.structuredData;
  }
};
